import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaCheckCircle, FaClock, FaMapMarkerAlt, FaEuroSign, FaCalendar } from 'react-icons/fa';

export default function MyQuotes() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyQuotes();
    }
  }, [user]);

  const fetchMyQuotes = async () => {
    try {
      const response = await axios.get(`${API}/projects/quotes/my-quotes/all?supplier_id=${user.id}`);
      setQuotes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', text: i18n.language === 'fr' ? 'En attente' : 'Pending' },
      accepted: { color: 'bg-[#635BFF]/10 text-[#5248d9]', text: i18n.language === 'fr' ? 'Accepté' : 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-700', text: i18n.language === 'fr' ? 'Rejeté' : 'Rejected' }
    };
    
    const badge = badges[status] || badges.pending;
    return <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Mes Devis Soumis' : 'My Submitted Quotes'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr' 
              ? 'Historique de tous vos devis soumis' 
              : 'History of all your submitted quotes'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaCalendar className="text-2xl text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' ? 'Total Devis' : 'Total Quotes'}
                  </p>
                  <p className="text-2xl font-bold text-[#0A2540]">{quotes.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#635BFF]/10 rounded-full flex items-center justify-center">
                  <FaEuroSign className="text-2xl text-[#635BFF]" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' ? 'Valeur Totale' : 'Total Value'}
                  </p>
                  <p className="text-2xl font-bold text-[#0A2540]">
                    {quotes.reduce((sum, q) => sum + (q.total || 0), 0).toFixed(2)}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FaClock className="text-2xl text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {i18n.language === 'fr' ? 'Dernier Devis' : 'Last Quote'}
                  </p>
                  <p className="text-sm font-bold text-[#0A2540]">
                    {quotes.length > 0 
                      ? new Date(quotes[0].created_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quotes List */}
        {quotes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 mb-4">
                {i18n.language === 'fr' 
                  ? 'Vous n\'avez pas encore soumis de devis' 
                  : 'You haven\'t submitted any quotes yet'}
              </p>
              <Link
                to="/supplier/opportunities"
                className="inline-block bg-[#635BFF] text-white px-6 py-2 rounded-lg hover:bg-[#5248d9]"
              >
                {i18n.language === 'fr' ? 'Voir les opportunités' : 'View opportunities'}
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">
                        {quote.project_name}
                      </h3>
                      <p className="text-gray-600 flex items-center gap-2 mb-2">
                        <FaMapMarkerAlt className="text-[#635BFF]" />
                        {quote.project_location}
                      </p>
                      <p className="text-sm text-gray-500">
                        <FaCalendar className="inline mr-2" />
                        {i18n.language === 'fr' ? 'Soumis le:' : 'Submitted on:'} {formatDate(quote.created_at)}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-3xl font-bold text-[#635BFF] mb-2">
                        {quote.total?.toFixed(2) || '0.00'}€
                      </p>
                      {quote.status && getStatusBadge(quote.status)}
                    </div>
                  </div>

                  {/* Quote Details */}
                  <div className="border-t pt-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Sous-total' : 'Subtotal'}</p>
                        <p className="font-semibold">{quote.subtotal?.toFixed(2) || '0.00'}€</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Taxes' : 'Tax'}</p>
                        <p className="font-semibold">{quote.tax?.toFixed(2) || '0.00'}€</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Délai livraison' : 'Delivery Time'}</p>
                        <p className="font-semibold">{quote.delivery_time || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Line Items Summary */}
                    {quote.line_items && quote.line_items.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          {i18n.language === 'fr' ? 'Articles proposés:' : 'Proposed items:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {quote.line_items.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="bg-gray-100 px-3 py-1 rounded text-sm">
                              {item.supply_name} ({item.quantity})
                            </span>
                          ))}
                          {quote.line_items.length > 3 && (
                            <span className="text-sm text-gray-500">
                              +{quote.line_items.length - 3} {i18n.language === 'fr' ? 'autres' : 'more'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Certifications */}
                    {quote.certifications && quote.certifications.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          {i18n.language === 'fr' ? 'Certifications:' : 'Certifications:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {quote.certifications.map((cert, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                              <FaCheckCircle className="inline mr-1" />
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quality Notes */}
                    {quote.quality_notes && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">
                          {i18n.language === 'fr' ? 'Notes qualité:' : 'Quality notes:'}
                        </p>
                        <p className="text-sm text-gray-600">{quote.quality_notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/supplier/quotes/${quote.id}`)}
                      className="text-[#0A2540] hover:underline text-sm font-semibold"
                    >
                      {i18n.language === 'fr' ? 'Ouvrir la discussion' : 'Open discussion'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
